import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell } from "lucide-react";
import { api } from "../lib/api";

/**
 * Notification bell — shown in the top-right of every dashboard.
 *
 * Polls for unread notifications and shows the count on the bell. Opening the
 * panel does not mark everything read; a notification is marked read when it
 * is clicked, or via "Mark all read".
 */
export default function NotificationBell({ className = "" }) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/api/notifications?limit=25");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch { /* silent — the bell must never break a page */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);   // refresh every minute
    return () => clearInterval(t);
  }, []);

  // Close when clicking outside
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const markRead = async (n) => {
    if (n.read) return;
    try {
      await api.post(`/api/notifications/${n.id}/read`);
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      setUnread((u) => Math.max(0, u - 1));
    } catch { /* ignore */ }
  };

  const markAll = async () => {
    try {
      await api.post("/api/notifications/read-all");
      setItems((prev) => prev.map((x) => ({ ...x, read: true })));
      setUnread(0);
    } catch { /* ignore */ }
  };

  return (
    <div className={`relative ${className}`} ref={boxRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 text-ink hover:text-maroon transition-colors"
        aria-label={`Notifications${unread ? ` (${unread} unread)` : ""}`}
        data-testid="notification-bell"
      >
        <Bell size={18} />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 bg-maroon text-pearl text-[10px] min-w-[16px] h-4 px-1 rounded-full flex items-center justify-center"
            data-testid="notification-count"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="fixed left-2 right-2 top-16 w-auto sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-80 max-h-[70vh] overflow-y-auto bg-white border border-line shadow-xl z-50"
          data-testid="notification-panel"
        >
          <div className="flex items-center justify-between px-4 py-2 border-b border-line">
            <span className="eyebrow text-[10px]">Notifications</span>
            {unread > 0 && (
              <button onClick={markAll} className="text-[11px] text-maroon underline" data-testid="notification-mark-all">
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-8 text-center text-xs text-ash">Nothing new right now.</div>
            ) : (
              items.map((n) => {
                const inner = (
                  <>
                    <div className="flex items-start gap-2">
                      {!n.read && <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-maroon shrink-0"/>}
                      <div className={n.read ? "opacity-60" : ""}>
                        <div className="text-sm font-medium text-ink">{n.title}</div>
                        {n.body && <div className="text-[11px] text-ash mt-0.5">{n.body}</div>}
                        <div className="text-[10px] text-ash mt-1">
                          {n.created_at ? new Date(n.created_at).toLocaleString() : ""}
                        </div>
                      </div>
                    </div>
                  </>
                );
                const cls = "block w-full text-left px-4 py-3 border-b border-line/60 hover:bg-pearl/60";
                return n.link ? (
                  <Link key={n.id} to={n.link} onClick={() => { markRead(n); setOpen(false); }} className={cls} data-testid={`notification-${n.id}`}>
                    {inner}
                  </Link>
                ) : (
                  <button key={n.id} onClick={() => markRead(n)} className={cls} data-testid={`notification-${n.id}`}>
                    {inner}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
